"use client"

import { Card, CardContent } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Star, Quote, Users, Globe, TrendingUp } from "lucide-react"
import { useState } from "react"

interface Testimonial {
  name: string
  role: string
  company: string
  content: string
  rating: number
  avatar: string
  gradient: string
}

const testimonials: Testimonial[] = [
  {
    name: "Sarah Chen",
    role: "Frontend Developer",
    company: "TechCorp",
    content: "Swaraah AI has revolutionized my event management workflow. What used to take days now takes hours. The AI understands exactly what I need for creating stunning event platforms.",
    rating: 5,
    avatar: "SC",
    gradient: "from-theme-primary to-theme-accent"
  },
  {
    name: "Marcus Rodriguez",
    role: "Startup Founder",
    company: "InnovateLab",
    content: "As a non-technical founder, Swaraah AI allowed me to create our event platform MVP without hiring a full development team. Incredible value and power!",
    rating: 5,
    avatar: "MR",
    gradient: "from-blue-500 to-cyan-500"
  },
  {
    name: "Emily Johnson",
    role: "Full Stack Developer",
    company: "DevStudio",
    content: "The drag-and-drop editor combined with AI code generation is a game-changer. I can focus on creative aspects while AI handles the infrastructure.",
    rating: 5,
    avatar: "EJ",
    gradient: "from-green-500 to-emerald-500"
  },
  {
    name: "David Kim",
    role: "Product Manager",
    company: "ScaleUp Inc",
    content: "Our team's productivity has increased by 300% since adopting Swaraah AI. The quality of generated event platforms is consistently outstanding.",
    rating: 5,
    avatar: "DK",
    gradient: "from-orange-500 to-red-500"
  },
  {
    name: "Lisa Wang",
    role: "UI/UX Designer",
    company: "DesignFlow",
    content: "Finally, a tool that bridges the gap between design and development. I can see my event designs come to life instantly with clean, maintainable code.",
    rating: 5,
    avatar: "LW",
    gradient: "from-theme-accent to-rose-500"
  },
  {
    name: "Alex Thompson",
    role: "CTO",
    company: "NextGen Solutions",
    content: "Swaraah AI has become an essential part of our tech stack. The enterprise features and security are exactly what we needed for our event platform.",
    rating: 5,
    avatar: "AT",
    gradient: "from-indigo-500 to-purple-500"
  }
]

interface TestimonialCardProps {
  testimonial: Testimonial
  index: number
}

const TestimonialCard = ({ testimonial, index }: TestimonialCardProps) => {
  const [isHovered, setIsHovered] = useState(false)

  return (
    <Card 
      className="group relative glass-card border backdrop-blur-sm hover:shadow-2xl transition-all duration-500 hover:-translate-y-1"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{
        borderColor: isHovered 
          ? `rgba(var(--theme-primary), 0.5)` 
          : `rgba(var(--border), 0.5)`
      }}
    >
      {/* Gradient Overlay on Hover */}
      <div 
        className="absolute inset-0 rounded-lg opacity-0 group-hover:opacity-5 transition-opacity duration-500"
        style={{
          background: `linear-gradient(135deg, rgb(var(--theme-primary)), rgb(var(--theme-secondary)))`
        }}
      />

      <CardContent className="relative p-6">
        {/* Quote Icon */}
        <div className="absolute top-4 right-4 opacity-10 group-hover:opacity-20 transition-opacity duration-300">
          <Quote 
            className="h-12 w-12 transform rotate-12" 
            style={{ color: `rgb(var(--theme-primary))` }}
          />
        </div>

        {/* Rating stars */}
        <div className="flex items-center mb-4 gap-1">
          {[...Array(testimonial.rating)].map((_, i) => (
            <Star 
              key={i} 
              className="h-4 w-4 fill-current transition-all duration-300 group-hover:scale-110" 
              style={{ 
                color: `rgb(var(--theme-accent))`,
                transitionDelay: `${i * 50}ms`
              }}
            />
          ))}
        </div>

        {/* Quote content */}
        <blockquote className="text-foreground/90 mb-6 leading-relaxed group-hover:text-foreground transition-colors duration-300">
          "{testimonial.content}"
        </blockquote>

        {/* Author info */}
        <div className="flex items-center gap-4">
          <div 
            className="flex h-12 w-12 items-center justify-center rounded-full text-white font-semibold text-sm shadow-lg transition-all duration-500 group-hover:scale-110 group-hover:rotate-6"
            style={{
              background: `linear-gradient(135deg, rgb(var(--theme-primary)), rgb(var(--theme-secondary)))`
            }}
          >
            {testimonial.avatar}
          </div>
          <div className="flex-1">
            <div className="font-semibold text-foreground group-hover:text-foreground transition-colors duration-300">
              {testimonial.name}
            </div>
            <div className="text-sm text-muted-foreground">
              {testimonial.role} at {testimonial.company}
            </div>
          </div>
        </div>

        {/* Bottom Border Animation */}
        <div 
          className="absolute bottom-0 left-0 h-1 w-0 group-hover:w-full transition-all duration-700 rounded-b-lg"
          style={{
            background: `linear-gradient(90deg, rgb(var(--theme-primary)), rgb(var(--theme-secondary)))`
          }}
        />
      </CardContent>
    </Card>
  )
}

export function TestimonialsSection() {
  const [isPaused, setIsPaused] = useState(false)

  // Split testimonials into columns
  const column1 = testimonials.slice(0, 2)
  const column2 = testimonials.slice(2, 4)
  const column3 = testimonials.slice(4, 6)

  return (
    <section className="relative py-24 overflow-hidden bg-background">
      {/* Animated Background */}
      <div className="absolute inset-0 opacity-30 dark:opacity-20">
        <div 
          className="absolute inset-0"
          style={{
            backgroundImage: `
              linear-gradient(90deg, rgba(var(--theme-primary), 0.02) 1px, transparent 1px),
              linear-gradient(rgba(var(--theme-primary), 0.02) 1px, transparent 1px)
            `,
            backgroundSize: '60px 60px'
          }}
        />
      </div>

      {/* Floating Gradient Orbs */}
      <div className="absolute top-1/4 left-0 w-96 h-96 rounded-full opacity-10 blur-3xl animate-pulse"
        style={{
          background: `radial-gradient(circle, rgb(var(--theme-primary)) 0%, transparent 70%)`,
          animationDuration: '8s'
        }}
      />
      <div className="absolute bottom-1/4 right-0 w-96 h-96 rounded-full opacity-10 blur-3xl animate-pulse"
        style={{
          background: `radial-gradient(circle, rgb(var(--theme-secondary)) 0%, transparent 70%)`,
          animationDuration: '6s',
          animationDelay: '2s'
        }}
      />

      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16 animate-in fade-in slide-in-from-bottom duration-700">
          <Badge 
            variant="secondary" 
            className="mb-6 px-4 py-2 text-sm glass-card border"
            style={{
              borderColor: `rgba(var(--theme-primary), 0.3)`,
              background: `rgba(var(--theme-primary), 0.05)`
            }}
          >
            <Users className="mr-2 h-4 w-4" style={{ color: `rgb(var(--theme-primary))` }} />
            Testimonials
          </Badge>
          
          <h2 className="text-3xl font-bold tracking-tight text-foreground sm:text-5xl mb-6">
            Loved by Event Organizers{" "}
            <span 
              className="relative inline-block"
              style={{
                background: `linear-gradient(135deg, rgb(var(--theme-primary)), rgb(var(--theme-secondary)))`,
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text'
              }}
            >
              Worldwide
              <svg
                className="absolute -bottom-2 left-0 w-full"
                height="8"
                viewBox="0 0 200 8"
                fill="none"
              >
                <path
                  d="M1 5.5C50 2.5 150 2.5 199 5.5"
                  stroke={`rgb(var(--theme-accent))`}
                  strokeWidth="3"
                  strokeLinecap="round"
                />
              </svg>
            </span>
          </h2>
          
          <p className="mx-auto max-w-3xl text-lg text-muted-foreground leading-relaxed">
            Join thousands of event professionals who are building faster and smarter with Swaraah AI.
          </p>
        </div>

        {/* Three-column scrolling testimonials */}
        <div 
          className="grid grid-cols-1 md:grid-cols-3 gap-8 max-h-[600px] overflow-hidden mb-16"
          onMouseEnter={() => setIsPaused(true)}
          onMouseLeave={() => setIsPaused(false)}
        >
          {/* Column 1 - Scrolling up */}
          <div className={`space-y-6 ${!isPaused ? 'animate-scroll-up' : ''}`}>
            {[...column1, ...column1, ...column1].map((testimonial, index) => (
              <TestimonialCard 
                key={`col1-${index}`} 
                testimonial={testimonial} 
                index={index} 
              />
            ))}
          </div>

          {/* Column 2 - Scrolling down */}
          <div className={`space-y-6 ${!isPaused ? 'animate-scroll-down' : ''}`}>
            {[...column2, ...column2, ...column2].map((testimonial, index) => (
              <TestimonialCard 
                key={`col2-${index}`} 
                testimonial={testimonial} 
                index={index} 
              />
            ))}
          </div>

          {/* Column 3 - Scrolling up (slower) */}
          <div className={`space-y-6 ${!isPaused ? 'animate-scroll-up-slow' : ''}`}>
            {[...column3, ...column3, ...column3].map((testimonial, index) => (
              <TestimonialCard 
                key={`col3-${index}`} 
                testimonial={testimonial} 
                index={index} 
              />
            ))}
          </div>
        </div>

        {/* Enhanced Stats Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-8 animate-in fade-in slide-in-from-bottom duration-700 delay-200">
          {[
            { value: "10,000+", label: "Event Organizers", icon: Users },
            { value: "4.9/5", label: "Average Rating", icon: Star },
            { value: "50+", label: "Countries", icon: Globe }
          ].map((stat, index) => {
            const IconComponent = stat.icon
            return (
              <Card key={index} className="group glass-card border backdrop-blur-sm hover:shadow-xl transition-all duration-500 hover:-translate-y-2 cursor-pointer">
                <CardContent className="p-8 text-center">
                  <div className="flex justify-center mb-4">
                    <div 
                      className="p-3 rounded-xl transition-all duration-500 group-hover:scale-110 group-hover:rotate-6"
                      style={{
                        background: `rgba(var(--theme-primary), 0.1)`
                      }}
                    >
                      <IconComponent 
                        className="h-6 w-6"
                        style={{ color: `rgb(var(--theme-primary))` }}
                      />
                    </div>
                  </div>
                  <div 
                    className="text-4xl font-bold mb-3 transition-all duration-500 group-hover:scale-110"
                    style={{
                      background: `linear-gradient(135deg, rgb(var(--theme-primary)), rgb(var(--theme-secondary)))`,
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      backgroundClip: 'text'
                    }}
                  >
                    {stat.value}
                  </div>
                  <div className="text-sm text-muted-foreground font-medium uppercase tracking-wide">
                    {stat.label}
                  </div>
                </CardContent>
              </Card>
            )
          })}
        </div>

        {/* Trust Badge */}
        <div className="mt-16 text-center animate-in fade-in slide-in-from-bottom duration-700 delay-400">
          <div className="inline-flex items-center gap-3 glass-card rounded-full px-6 py-3 border"
            style={{
              borderColor: `rgba(var(--theme-primary), 0.3)`,
              background: `rgba(var(--theme-primary), 0.05)`
            }}
          >
            <TrendingUp className="h-5 w-5" style={{ color: `rgb(var(--theme-primary))` }} />
            <span className="text-sm font-semibold text-foreground">
              Trusted by leading event professionals worldwide
            </span>
          </div>
        </div>
      </div>

      {/* Custom scrolling animations */}
      <style>{`
        @keyframes scrollUp {
          0% {
            transform: translateY(0);
          }
          100% {
            transform: translateY(calc(-33.333%));
          }
        }

        @keyframes scrollDown {
          0% {
            transform: translateY(calc(-33.333%));
          }
          100% {
            transform: translateY(0);
          }
        }

        @keyframes scrollUpSlow {
          0% {
            transform: translateY(0);
          }
          100% {
            transform: translateY(calc(-33.333%));
          }
        }

        .animate-scroll-up {
          animation: scrollUp 25s linear infinite;
        }

        .animate-scroll-down {
          animation: scrollDown 25s linear infinite;
        }

        .animate-scroll-up-slow {
          animation: scrollUpSlow 30s linear infinite;
        }
      `}</style>
    </section>
  )
}
